import { ApiClient, IShip, IShipImportStatus, IShipUserLevel, ILocalShipResolveResponse, ISessionResponse, Ship, ShipApi } from "../../Types"
import { ShipSelectRow, ShipSelectRowParams, ShipSelectState, ShipSelectUnavailable } from "./Params"

// Shared data layer for ShipSelectView and its child views: the network requests plus
// the pure row-state decision. Extracted so the wire contract and every branch can be
// unit-tested without rendering (mirrors AccountSettings/Context.tsx).
export const ShipSelectViewData = {
  // Pair with the primary. `address` is omitted for an automatic (Bonjour) attempt and
  // set for a manual one. `attempt` is an opaque id the app-local route re-checks
  // immediately before it persists `local:ship`/`local:session`, so a cancelled,
  // timed-out or superseded attempt can never commit pairing state late (#1279).
  connectLocal(api: ApiClient, shipId: number, address?: string, attempt?: string): Promise<ISessionResponse> {
    return new ShipApi(api).ships.connectLocal({ params: { shipId }, body: { address, attempt } }).call();
  },
  async cancelLocal(api: ApiClient, shipId: number, attempt?: string): Promise<void> {
    await new ShipApi(api).ships.disconnectLocal({ params: { shipId }, body: { attempt } }).call();
  },
  prepareHandoff(api: ApiClient, shipId: number) {
    return new ShipApi(api).ships.prepareHandoff({ params: { shipId } }).call();
  },
  // Resolve the ship's advertised service to a `host:port` WITHOUT pairing. Splitting
  // resolution from the connect is what lets the dialog prefill the manual form even
  // when the subsequent login is slow or never answers. When there is no address the
  // response says WHY, which is what the dialog reports after an automatic attempt
  // falls back (#1388).
  resolveLocal(api: ApiClient, shipId: number): Promise<ILocalShipResolveResponse> {
    return new ShipApi(api).ships.resolveLocal({ params: { shipId } }).call();
  },
};

// True while an import is still running. A complete or failed import is finished --
// failed imports must stay openable so the ship can be opened and the import retried.
function importInProgress(ship: IShip): boolean {
  const status = Ship.parse(ship).dataParsed?.importStatus;
  return !!status && status != IShipImportStatus.complete && status != IShipImportStatus.failed;
}

// The whole row decision, as one pure function of already-loaded data. `ownShipId`
// comes from `IShipsResponse.ownShipId`: `app.shipId` is cleared before the ships list
// drops its stale `client_token`, and it was that gap that let a device offer its OWN
// ship as a connectable companion (#1279).
export function shipSelectRow(params: ShipSelectRowParams): ShipSelectRow {
  const { ship, user, ownShipId, nearbyShipIds, primaryFeature, viewable } = params;
  // Discovery does not exclude this device's own advertisement: `/api/ships/local`
  // browses the LAN for the account's ships and our own service answers. For the ship
  // THIS device holds, "found on your network" would therefore be a statement about
  // another device made about ourselves -- it rendered next to the logbook the user
  // had just opened here. The badge is only ever about someone else's device.
  const nearby = !!nearbyShipIds && nearbyShipIds.indexOf(ship.id) >= 0 && ownShipId !== ship.id;
  const row = (state: ShipSelectState, unavailable?: ShipSelectUnavailable): ShipSelectRow =>
    ({ state, unavailable, nearby });
  // Holding a logbook open blocks every OTHER row. The row keeps its own state so it
  // still renders its proper control and icons -- just disabled, with the reason.
  const blockedByOpenShip = ownShipId != undefined && ownShipId !== ship.id;
  const rowBlockedIfOtherOpen = (state: ShipSelectState): ShipSelectRow =>
    ({ state, blocked: blockedByOpenShip ? ShipSelectUnavailable.shipOpenHere : undefined, nearby });

  if (viewable === false) {
    return row(ShipSelectState.unavailable);
  }
  if (importInProgress(ship)) {
    return row(ShipSelectState.unavailable, ShipSelectUnavailable.importInProgress);
  }
  // Ownership is checked before the claim token, so the stale-token window resolves to
  // "mine" rather than "someone else's".
  if (ownShipId != undefined && ownShipId === ship.id) {
    return row(ShipSelectState.openHere);
  }
  if (ship.clientToken != undefined) {
    // Deliberately not gated on a membership level: the server authorizes the pairing,
    // and the ships list only contains ships this user can already see. A client-side
    // gate here would strand a user whose membership hasn't loaded yet.
    return rowBlockedIfOtherOpen(ShipSelectState.openElsewhere);
  }
  if ((user?.shipUserLevel ?? IShipUserLevel.none) >= IShipUserLevel.primary && primaryFeature === true) {
    return rowBlockedIfOtherOpen(ShipSelectState.openable);
  }
  return row(ShipSelectState.unavailable, ShipSelectUnavailable.noPermission);
}
